import type { WorkspaceSettingsTabsType } from 'components/pages/SchemasV2/WorkspaceSettings/WorkspaceSettingsModal';
import {
  WORKSPACE_SETTINGS_MODAL_URL_PREFIX,
  WorkspaceSettingsModal,
  WorkspaceSettingsTabs,
} from 'components/pages/SchemasV2/WorkspaceSettings/WorkspaceSettingsModal';
import { DecodedParamsRoute } from 'components/routing/DecodedParamsRoute';
import {
  type ActivePath,
  ACTIVE_PATH_URLS,
} from 'components/SideNav/ActivePaths';
import { NavButton } from 'components/SideNav/NavButton';
import { SideNav } from 'components/SideNav/SideNav';
import { useDashboardNavTree } from 'components/SideNav/useDashboardNavTree';
import React, {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { makePath } from 'utils/routes';
import { Route, Router, Switch, useLocation, useRoute } from 'wouter';
import { useApiExplorerContext } from 'wrappers/ApiExplorerProvider';
import { z } from 'zod';

type RouteConfig = {
  url: string;
  active: ActivePath;
};

const createNavForURL = (
  workspaceId: string,
  base: string,
  path: string,
  active: ActivePath,
  setIsAPIExplorerOpen: (isOpen: boolean) => void,
  handleOpenSettings: () => void,
  handleCloseSettings: () => void,
  isSettingsOpen: boolean,
  isAPIExplorerOpen: boolean,
  settingsTab: WorkspaceSettingsTabsType,
  setSettingsTab: (tab: WorkspaceSettingsTabsType) => void
) => (
  <DecodedParamsRoute path={`${base}${path}`} key={`${base}${path}`}>
    {(params) => {
      const {
        schemaKey,
        ledgerId,
        accountId,
        linkId,
        groupId,
        externalAccountId,
      } = params;

      const navTree = useDashboardNavTree({
        active,
        workspaceId,
        ledgerId,
        accountId,
        linkId,
        groupId,
        externalAccountId,
        schemaKey,
      });
      return (
        <div className="flex flex-col" data-testid="main-nav">
          <SideNav navTree={navTree} depth={0} />
          <NavButton
            onClick={() => setIsAPIExplorerOpen(true)}
            marker="numeric"
            ordinal={2}
            label="API Explorer"
            isActive={isAPIExplorerOpen}
            testId="main-nav-explorer-button"
          />
          <NavButton
            onClick={() => {
              setSettingsTab('members');
              handleOpenSettings();
            }}
            marker="numeric"
            ordinal={3}
            label="Members"
            isActive={isSettingsOpen && settingsTab === 'members'}
            testId="main-nav-members-button"
          />
          <NavButton
            onClick={() => {
              setSettingsTab('workspace');
              handleOpenSettings();
            }}
            marker="numeric"
            ordinal={4}
            label="Settings"
            isActive={isSettingsOpen && settingsTab === 'workspace'}
            testId="main-nav-settings-button"
          />
          {isSettingsOpen && (
            <WorkspaceSettingsModal
              handleClose={handleCloseSettings}
              defaultTab={settingsTab}
            />
          )}
        </div>
      );
    }}
  </DecodedParamsRoute>
);

type MainNavProps = {
  workspaceId: string;
};

export const MainNav = ({ workspaceId }: MainNavProps) => {
  const base = makePath(['w', workspaceId]);

  const { isOpen: isAPIExplorerOpen, setIsOpen: setIsAPIExplorerOpen } =
    useApiExplorerContext();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const urlsAndPaths: RouteConfig[] = Object.entries(ACTIVE_PATH_URLS)
    .map(([activePath, url]) =>
      url.map((u) => ({
        active: activePath as ActivePath,
        url: u,
      }))
    )
    .flat();

  const handleCloseSettings = useCallback(
    () => startTransition(() => setIsSettingsOpen(false)),
    []
  );
  const handleOpenSettings = useCallback(
    () => startTransition(() => setIsSettingsOpen(true)),
    []
  );

  useHotkeys('esc', handleCloseSettings, {
    enabled: true,
  });

  const TabRouteSchema = z.enum(WorkspaceSettingsTabs).nullish();
  const [settingsTab, setSettingsTab] =
    useState<WorkspaceSettingsTabsType>('workspace');

  const [pathMatch, params] = useRoute(
    `${WORKSPACE_SETTINGS_MODAL_URL_PREFIX}/:tab*`
  );
  const [, navigate] = useLocation();
  useEffect(() => {
    if (pathMatch) {
      const parsedTabRoute = TabRouteSchema.safeParse(params.tab);
      const defaultSettingsTab = parsedTabRoute.success
        ? parsedTabRoute.data
        : undefined;
      // We received an invalid value in the slug.
      if (parsedTabRoute.error) {
        navigate(WORKSPACE_SETTINGS_MODAL_URL_PREFIX, { replace: true });
      }
      setSettingsTab(defaultSettingsTab ?? 'workspace');
      setIsSettingsOpen(true);
    }
  }, [pathMatch, navigate, params?.tab, TabRouteSchema]);

  const routes = useMemo(
    () =>
      urlsAndPaths.map(({ url, active }) =>
        createNavForURL(
          workspaceId,
          base,
          url,
          active,
          setIsAPIExplorerOpen,
          handleOpenSettings,
          handleCloseSettings,
          isSettingsOpen,
          isAPIExplorerOpen,
          settingsTab,
          setSettingsTab
        )
      ),
    [
      urlsAndPaths,
      workspaceId,
      base,
      setIsAPIExplorerOpen,
      handleOpenSettings,
      handleCloseSettings,
      isSettingsOpen,
      isAPIExplorerOpen,
      settingsTab,
      setSettingsTab,
    ]
  );

  return (
    <Router>
      <Switch>{routes}</Switch>
      <Route>
        {/* This is rendered when nothing above has matched, can we throw some type of error here that we'd see in our alerts? */}
        {createNavForURL(
          workspaceId,
          base,
          '/',
          'projects',
          setIsAPIExplorerOpen,
          handleOpenSettings,
          handleCloseSettings,
          isSettingsOpen,
          isAPIExplorerOpen,
          settingsTab,
          setSettingsTab
        )}
      </Route>
    </Router>
  );
};
